import {
  reactive,
  Ref,
  ref,
  computed,
  UnwrapRef,
  watch,
  onUnmounted,
  ComponentInternalInstance,
  getCurrentInstance,
  onMounted,
} from '@vue/composition-api';
import { DialogPlugin } from 'tdesign-vue';
import { drawerHandler } from '@/renderer/scripts/composition/drawerHandler';
import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';
import tips from '@/renderer/scripts/utils/tips';
import smh, { SMH } from '@/renderer/scripts/smh';
import ipc from '@/renderer/scripts/utils/ipc';
import { DrawerEvents } from '@/renderer/store/modules/drawer';
import i18n, { i18n_t } from '@/locales/i18n';
import CustomDialog from '@/renderer/scripts/utils/CustomDialog';

type ListItem = SMH.ColleborationSpaceInfoData & {
  checked: boolean;
};
interface TableData {
  list: Array<ListItem>;
}
type PageData = {
  currPage: number;
  pageSize: number;
  total: number;
  pageSizeOptions: Array<any>;
};
type JoinTimeParams = {
  joinTimeStart?: string;
  joinTimeEnd?: string;
};
type ConditionParams = {
  joinType: string;
  joinTime: string;
  joinTimeParams?: JoinTimeParams;
};
type Handler = {
  methods: {
    pullList: (params?: any, silentRefresh?: boolean) => Promise<void>;
    disbandCoSpace: (data: ListItem) => Promise<void>;
    onPageSizeChange: (pageSize: number) => void;
    onCurrentChange: (current: number) => Promise<void>;
  };
  show: Ref<boolean>;
  loading: Ref<boolean>;
  loadingOnHeader: Ref<boolean>; // 在顶部loading，loading期间list内容不变
  tableData: UnwrapRef<TableData>;
  pageData: UnwrapRef<PageData>;
  selected: Ref<ListItem>;
  conditionParams: UnwrapRef<ConditionParams>;
};
export default function listHandler(): Handler {
  const loading = ref(false);
  const loadingOnHeader = ref(false);
  const show = ref(false);
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const store = vm.$store;
  const tableData = reactive<TableData>({
    list: [],
  });
  const selected = computed(() => tableData.list.filter((i: any) => i.checked)[0]);
  const pageData = reactive<PageData>({
    currPage: 1,
    pageSize: 10,
    total: 0,
    pageSizeOptions: [
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 10 }),
        value: 10,
      },
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 20 }),
        value: 20,
      },
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 30 }),
        value: 30,
      },
    ],
  });
  const conditionParams = reactive<ConditionParams>({
    joinType: 'all',
    joinTime: 'all',
  });
  const methods = {
    async pullList(params?: any, silentRefresh = false) {
      const opt = {
        page: params?.currPage || pageData.currPage,
        pageSize: params?.pageSize || pageData.pageSize,
        withFileCount: 1,
        withDirectory: 1,
        checkUpdateRecursively: 1,
        joinType: conditionParams.joinType,
        ...conditionParams.joinTimeParams,
      };

      loading.value = !silentRefresh;
      loadingOnHeader.value = silentRefresh;
      try {
        const { data: listRes } = await smh.userAPI.listCollaborationSpaces(opt);
        tableData.list = listRes.contents.map(item => ({
          ...item,
          checked: false,
        }));
        pageData.total = listRes.totalNum;
        ipc.emit(ipc.RENDERER_EVENTS_MAP.ON_DATA_LOADED, listRes.totalNum);
        if (loadingOnHeader.value) {
          tips.success(i18n_t('刷新成功'));
        }
      } catch (error) {
        tips.error(error as any);
      }
      loading.value = false;
      loadingOnHeader.value = false;
    },
    async disbandCoSpace() {
      const disband = async () => {
        const groupId = selected.value.id;
        tips.loading(i18n_t('操作中'), 0);
        try {
          await smh.userAPI.deleteCollaborationSpace({
            groupId,
          });
          tips.success(i18n_t('解散成功'));
          // 避免删除当前页最后一条数据时由于未跳转翻页导致展示空列表
          if (
            tableData.list.findIndex(item => item.id === groupId) > -1 &&
            tableData.list.length === 1 &&
            pageData.currPage !== 1
          ) {
            await methods.onCurrentChange(pageData.currPage - 1);
          } else {
            await methods.pullList();
          }
        } catch (error) {
          tips.error(error as MsgError);
        }
      };
      const groupName = selected.value.name;
      const dialog = CustomDialog.confirm({
        header: i18n_t('提示'),
        body: (h: any) =>
          h('div', [
            h(
              'h2',
              {
                attrs: {
                  class: 'dialog-overflow',
                },
              },
              i18n_t('确认要解散“{groupName}”吗？', {
                groupName,
              }),
            ),

            h('p', i18n_t('解散后，群组文件将被彻底删除，不可被找回')),
          ]),

        theme: 'warning',
        onConfirm: () => {
          if (dialog.hide) {
            dialog.hide();
          }
          disband();
        },
      });
    },
    async onPageSizeChange(pageSize: number) {
      pageData.pageSize = pageSize;
      await methods.pullList();
    },
    async onCurrentChange(current: number) {
      pageData.currPage = current;
      await methods.pullList();
    },
  };

  // 抽屉相关的代码块
  const { drawerHandlerMethods } = drawerHandler();
  // 初始化的时候，立刻初始化详情组件
  const emptyScope: Drawer.EmptyScope = {
    title: '',
    componentName: 'EmptyDetail',
    params: {
      totalMsg: i18n_t('共有0个文件'),
      title: i18n_t('查看详情信息'),
      desc: i18n_t('选中文件/文件夹查看更详细信息'),
      imgClass: '',
    },
  };
  drawerHandlerMethods.setDrawers([emptyScope]);
  // 当数据全部加载完毕
  const setTotal = (total: number) => {
    store.commit(DrawerEvents.SET_TOTAL, total);
  };
  ipc.on(ipc.RENDERER_EVENTS_MAP.ON_DATA_LOADED, setTotal);

  // 事件卸载
  // 监听选中项
  const onSelectedChanged = (selected: ListItem) => {
    if (!selected) {
      drawerHandlerMethods.setDrawers([emptyScope]);
    } else {
      const dynamicScope: Drawer.CollaborationDynamicScope<SMH.ColleborationSpaceInfoData> = {
        title: i18n_t('动态'),
        componentName: 'Dynamic',
        params: {
          selectedItem: selected,
          spaceId: selected.spaceId,
          spaceOrgId: selected.orgId,
          buttonTitle:
            selected.ownerId === vm.$store.getters.userId && selected.orgId === vm.$store.getters.orgId
              ? i18n_t('外链管理')
              : undefined,
          buttonEvent: () => {
            dispatchRouter({ name: 'collaborationShareList' });
          },
        },
      };
      const authScope: Drawer.CollaborationAuthScope<SMH.ColleborationSpaceInfoData> = {
        title: i18n_t('成员管理'),
        componentName: 'CollaborationMemberManage',
        params: {
          selectedItem: selected,
          spaceId: selected.spaceId,
          groupId: selected.id,
        },
      };
      drawerHandlerMethods.setDrawers([dynamicScope, authScope]);
    }
  };
  watch(selected, onSelectedChanged);
  onMounted(() => {
    drawerHandlerMethods.setDrawers([emptyScope]);
  });
  onUnmounted(() => {
    ipc.off(ipc.RENDERER_EVENTS_MAP.ON_SELECTED_CHANGE, onSelectedChanged);
    ipc.off(ipc.RENDERER_EVENTS_MAP.ON_DATA_LOADED, setTotal);
  });
  return {
    show,
    tableData,
    pageData,
    selected,
    loading,
    loadingOnHeader,
    methods,
    conditionParams,
  };
}
